import type { AuthAdapter, AdapterResult, AdapterOptions } from "./types";
import type { AuthProvider } from "../types";

/**
 * redirect 登录发起前记下 provider —— 页面会整页跳走，回来后 `AuthOrchestrator.checkRedirectResult`
 * 靠它判断「本 tab 确实发起过 redirect」并拿到登录方式（成功 / 失败埋点都要）。
 */
const PENDING_REDIRECT_PROVIDER_KEY = "edw.auth.pending_redirect_provider";

function markPendingRedirect(provider: AuthProvider): void {
  try {
    sessionStorage.setItem(PENDING_REDIRECT_PROVIDER_KEY, provider);
  } catch {
    // 无痕模式等 storage 不可用：只影响回跳埋点，不影响登录
  }
}

function clearPendingRedirect(): void {
  try {
    sessionStorage.removeItem(PENDING_REDIRECT_PROVIDER_KEY);
  } catch {
    // storage 不可用时本来也没写进去
  }
}

export function consumePendingRedirectProvider(): AuthProvider | null {
  try {
    const raw = sessionStorage.getItem(PENDING_REDIRECT_PROVIDER_KEY);
    sessionStorage.removeItem(PENDING_REDIRECT_PROVIDER_KEY);
    return raw === "google" || raw === "apple" || raw === "twitter"
      ? raw
      : null;
  } catch {
    return null;
  }
}

export class FirebaseSocialAdapter implements AuthAdapter {
  readonly provider: AuthProvider;

  constructor(provider: "google" | "apple" | "twitter") {
    this.provider = provider;
  }

  async authenticate(options?: AdapterOptions): Promise<AdapterResult> {
    const { getAuth, signInWithPopup, signInWithRedirect, getRedirectResult } =
      await import("firebase/auth");
    const auth = getAuth();
    const authProvider = await this.createProvider(options);

    if (shouldUseRedirect()) {
      await this.startRedirect(() => signInWithRedirect(auth, authProvider));
      const result = await getRedirectResult(auth);
      if (!result) {
        throw new Error("Redirect login returned no result");
      }
      const idToken = await result.user.getIdToken();
      return this.buildResult(result, idToken);
    }

    try {
      const result = await signInWithPopup(auth, authProvider);
      const idToken = await result.user.getIdToken();
      return this.buildResult(result, idToken);
    } catch (error: any) {
      if (isPopupBlocked(error)) {
        await this.startRedirect(() => signInWithRedirect(auth, authProvider));
        const result = await getRedirectResult(auth);
        if (!result) {
          throw new Error("Redirect login returned no result");
        }
        const idToken = await result.user.getIdToken();
        return this.buildResult(result, idToken);
      }
      throw error;
    }
  }

  /**
   * 记下 provider 后发起整页 redirect。`signInWithRedirect` 直接 reject 时页面没有跳走，
   * 这次失败已由 `login()` 上报；不清标记的话，下次加载 `checkRedirectResult` 会把同一次失败再报一遍。
   */
  private async startRedirect(redirect: () => Promise<unknown>): Promise<void> {
    markPendingRedirect(this.provider);
    try {
      await redirect();
    } catch (error) {
      clearPendingRedirect();
      throw error;
    }
  }

  private buildResult(
    result: import("firebase/auth").UserCredential,
    idToken: string,
  ): AdapterResult {
    return {
      authHeaders: { "OS-Firebase-Token": idToken },
      providerUser: {
        email: result.user.email ?? undefined,
        displayName: result.user.displayName ?? undefined,
        photoUrl: result.user.photoURL ?? undefined,
        providerId: result.user.providerData[0]?.providerId,
      },
    };
  }

  private async createProvider(
    options?: AdapterOptions,
  ): Promise<import("firebase/auth").AuthProvider> {
    const { GoogleAuthProvider, OAuthProvider } = await import("firebase/auth");

    switch (this.provider) {
      case "google": {
        const p = new GoogleAuthProvider();
        if (options?.forceAccountSelection) {
          p.setCustomParameters({ prompt: "select_account" });
        }
        return p;
      }
      case "apple":
        return new OAuthProvider("apple.com");
      case "twitter":
        return new OAuthProvider("twitter.com");
      default:
        throw new Error(`Unsupported provider: ${this.provider}`);
    }
  }
}

function shouldUseRedirect(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const isStandalone =
    (window.navigator as any).standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches;
  return isStandalone;
}

/**
 * 只有「弹窗被浏览器拦截」才退回整页 redirect。
 *
 * `auth/popup-closed-by-user` 是用户主动关掉弹窗（取消），原样抛出 —— 由 AuthOrchestrator 记
 * `auth_provider_result{cancel}`、用户留在原页面。此前它也被当成「被拦截」：用户关了弹窗反而被整页
 * 带去 Google/Apple 授权页，而且这次取消在埋点上完全看不到。
 */
function isPopupBlocked(error: any): boolean {
  return (error?.code ?? "") === "auth/popup-blocked";
}
